// Tab Productividad — actividad real de la fiscalía en el período seleccionado.
// Todas las métricas vienen de columnas exactas (Movimientos.FechaDT,
// Documentos_Pendientes_Firma.Fecha_Firma) — no hay proxies ni estimaciones.
//
// Recuperación mes a mes NO está acá: requiere fecha real de cobro de cuotas,
// dato que vive en el sistema externo de Rentas (DGR / WSDGR). Cuando exista
// la integración, se agrega como 5° tarjeta sin cambiar el resto.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { dashEjecProductividadJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');

const PRESET_LABEL = { mes: 'Mes actual', trimestre: 'Últimos 3 meses', ano: 'Año actual', anoAnt: 'Año anterior', custom: 'Período seleccionado' };

export default function TabProductividad({ desde, hasta, preset, onClickAbogado }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        dashEjecProductividadJWT(desde, hasta)
            .then(r => { if (!cancel && r?.ok) setData(r.data); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [desde, hasta]);

    if (loading) {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[1, 2, 3].map(i => (
                        <Grid key={i} size={{ xs: 12, md: 4 }}>
                            <Skeleton variant="rounded" height={100} sx={{ borderRadius: `${BR}px` }} />
                        </Grid>
                    ))}
                </Grid>
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${BR}px` }} />
            </Box>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    const tot = data.totales || {};
    const periodLabel = PRESET_LABEL[preset] || 'Período';

    return (
        <Box>
            {/* Cards principales */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2.5, borderRadius: `${BR}px`,
                        background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)', color: 'white' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            📋 Movimientos — {periodLabel}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{fmtNum(tot.movimientos)}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 1 }}>
                            Toda la actividad registrada en expedientes
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2.5, borderRadius: `${BR}px`,
                        background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            ✍️ Documentos firmados — {periodLabel}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{fmtNum(tot.firmas)}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 1, fontSize: '0.7rem' }}>
                            Firmas digitales del Fiscal · solo desde la puesta en marcha del nuevo sistema
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2.5, borderRadius: `${BR}px`,
                        background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)', color: 'white' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            👥 Personal activo — {periodLabel}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{fmtNum(tot.personalActivo)}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 1 }}>
                            Generaron al menos un movimiento
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Tendencia mensual */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                    Actividad mensual — {periodLabel}
                </Typography>
                {(data.tendencia || []).length > 0 ? (
                    <LineChart
                        height={300}
                        xAxis={[{ scaleType: 'point', data: (data.tendencia || []).map(t => t.mes) }]}
                        series={[
                            { data: (data.tendencia || []).map(t => Number(t.movimientos || 0)), label: 'Movimientos', color: '#1565c0' },
                            { data: (data.tendencia || []).map(t => Number(t.firmas || 0)), label: 'Documentos firmados', color: '#37474f' },
                        ]}
                        margin={{ top: 20, right: 20, bottom: 30, left: 50 }}
                    />
                ) : <Typography color="text.secondary">Sin datos en el período seleccionado</Typography>}
            </Paper>

            {/* Top abogados + Tipos de movimiento */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Top 10 personal con más movimientos — {periodLabel}
                        </Typography>
                        <TableContainer sx={{ maxHeight: 440 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Función</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Área</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Movs.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.topPersonal || []).map((a, i) => (
                                        <TableRow key={a.idPersonal || i} hover
                                            sx={{ cursor: a.idPersonal && onClickAbogado ? 'pointer' : 'default' }}
                                            onClick={() => a.idPersonal && onClickAbogado && onClickAbogado(a.idPersonal)}>
                                            <TableCell sx={{ fontSize: '0.78rem' }}>{i + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{a.nombre}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#555' }}>{a.Funcion || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', color: '#555' }}>{a.Area || '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>
                                                {fmtNum(a.cant)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(data.topPersonal || []).length === 0 && (
                                        <TableRow><TableCell colSpan={5} sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                                            Sin movimientos en el período
                                        </TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Top 10 tipos de movimiento — {periodLabel}
                        </Typography>
                        <TableContainer sx={{ maxHeight: 440 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cant.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.tiposMovimiento || []).map((t, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{t.tipo}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>
                                                {fmtNum(t.cant)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(data.tiposMovimiento || []).length === 0 && (
                                        <TableRow><TableCell colSpan={2} sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                                            Sin datos en el período
                                        </TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
